/** @module @liquidefy/code-editor/ai/ai-request @version 1.0.0 @license LQD Research LLC */

// ═══ §1 IMPORTS ═══

import type {
  AIRequest,
  AIRequestType,
  AIModule,
  AIContext,
  AIConstraints,
  AIPriority,
  AIRequestId,
  AIModelSpecs,
  WorkspaceId,
  LanguageId,
  Position,
  Selection,
  EditorResult,
  DiagnosticInfo,
} from '../../code-editor-types.js';

import {
  ok,
  err,
  generateId,
  asAIRequestId,
  now,
} from '../../code-editor.js';

// ═══ §2 REQUEST CONSTRUCTION — create validated AIRequest objects ═══

const ALL_MODULES: readonly AIModule[] = [
  'editor', 'file', 'search', 'terminal', 'collaboration', 'project', 'workspace', 'git', 'review',
];

const ALL_REQUEST_TYPES: readonly AIRequestType[] = [
  'completion', 'suggestion', 'analysis', 'generation', 'conversation',
  'refactor', 'explain', 'fix', 'optimize', 'translate', 'document', 'test-generate',
];

/**
 * Create a raw AIRequest with generated ID and timestamp.
 */
export function createRequest(
  module: AIModule,
  type: AIRequestType,
  context: AIContext,
  overrides?: Partial<Pick<AIRequest, 'constraints' | 'priority' | 'qualityThreshold'>>,
): AIRequest {
  return {
    id: asAIRequestId(generateId('aireq')),
    module,
    type,
    context,
    constraints: overrides?.constraints,
    priority: overrides?.priority ?? 'normal',
    qualityThreshold: overrides?.qualityThreshold ?? 0.5,
    timestamp: now(),
  };
}

/**
 * Create a completion request for inline code completion.
 */
export function createCompletionRequest(context: AIContext): AIRequest {
  return createRequest('editor', 'completion', context, { priority: 'high' });
}

/**
 * Create an analysis request for the given analysis type.
 */
export function createAnalysisRequest(context: AIContext, analysisType: string): AIRequest {
  return createRequest('editor', 'analysis', context, {
    priority: analysisType === 'security' ? 'high' : 'normal',
    qualityThreshold: 0.6,
  });
}

/**
 * Create a generation request for the given generation type.
 */
export function createGenerationRequest(context: AIContext, generationType: string): AIRequest {
  const priority: AIPriority = generationType === 'fix' ? 'high' : 'normal';
  return createRequest('editor', 'generation', context, { priority, qualityThreshold: 0.7 });
}

/**
 * Create a conversation request.
 */
export function createConversationRequest(context: AIContext): AIRequest {
  return createRequest('editor', 'conversation', context, { priority: 'normal' });
}

/**
 * Create a refactor request with the specified refactor kind.
 */
export function createRefactorRequest(context: AIContext, _refactorKind: string): AIRequest {
  return createRequest('editor', 'refactor', context, { priority: 'normal', qualityThreshold: 0.7 });
}

/**
 * Create an explain request.
 */
export function createExplainRequest(context: AIContext): AIRequest {
  return createRequest('editor', 'explain', context, { priority: 'low', qualityThreshold: 0.5 });
}

/**
 * Create a fix request with diagnostics context.
 */
export function createFixRequest(context: AIContext, diagnostics: readonly DiagnosticInfo[]): AIRequest {
  const hasCritical = diagnostics.some((d) => d.severity === 'error');
  return createRequest('editor', 'fix', context, {
    priority: hasCritical ? 'critical' : 'high',
    qualityThreshold: 0.8,
  });
}

// ═══ §3 REQUEST VALIDATION — validate required fields and constraints ═══

/**
 * Validate a request has all required fields and coherent values.
 */
export function validateRequest(request: AIRequest): EditorResult<AIRequest> {
  if (!request.id) {
    return err('INVALID_REQUEST', 'Request must have an id');
  }
  if (!isValidModule(request.module)) {
    return err('INVALID_MODULE', `Unknown module: ${request.module}`);
  }
  if (!isValidRequestType(request.type)) {
    return err('INVALID_TYPE', `Unknown request type: ${request.type}`);
  }
  const contextResult = validateContext(request.context);
  if (!contextResult.success) {
    return contextResult as EditorResult<AIRequest>;
  }
  if (request.constraints) {
    const constraintResult = validateConstraints(request.constraints);
    if (!constraintResult.success) {
      return constraintResult as EditorResult<AIRequest>;
    }
  }
  if (request.qualityThreshold < 0 || request.qualityThreshold > 1) {
    return err('INVALID_THRESHOLD', 'Quality threshold must be between 0 and 1');
  }
  return ok(request, 'REQUEST_VALID', 'Request is valid');
}

/**
 * Check if the module string is a valid AIModule.
 */
export function isValidModule(module: string): module is AIModule {
  return ALL_MODULES.includes(module as AIModule);
}

/**
 * Check if the type string is a valid AIRequestType.
 */
export function isValidRequestType(type: string): type is AIRequestType {
  return ALL_REQUEST_TYPES.includes(type as AIRequestType);
}

/**
 * Validate context object — at minimum it should be a non-null object.
 */
export function validateContext(context: AIContext): EditorResult<AIContext> {
  if (context === null || context === undefined) {
    return err('INVALID_CONTEXT', 'Context must be provided');
  }
  if (typeof context !== 'object') {
    return err('INVALID_CONTEXT', 'Context must be an object');
  }
  return ok(context, 'CONTEXT_VALID', 'Context is valid');
}

/**
 * Validate constraints — ensure numeric values are positive.
 */
export function validateConstraints(constraints: AIConstraints): EditorResult<AIConstraints> {
  if (constraints.maxTokens !== undefined && constraints.maxTokens <= 0) {
    return err('INVALID_CONSTRAINT', 'maxTokens must be positive');
  }
  if (constraints.maxLatencyMs !== undefined && constraints.maxLatencyMs <= 0) {
    return err('INVALID_CONSTRAINT', 'maxLatencyMs must be positive');
  }
  if (constraints.maxCost !== undefined && constraints.maxCost < 0) {
    return err('INVALID_CONSTRAINT', 'maxCost must be non-negative');
  }
  return ok(constraints, 'CONSTRAINTS_VALID', 'Constraints are valid');
}

/**
 * Estimate the cost of a request based on model specs.
 */
export function estimateRequestCost(
  request: AIRequest,
  modelSpecs: AIModelSpecs,
): number {
  const baseTokens = request.constraints?.maxTokens ?? modelSpecs.maxOutputTokens;
  const inputEstimate = estimateInputTokens(request);
  const totalTokens = inputEstimate + baseTokens;
  // Rough cost estimate: $0.01 per 1K tokens as baseline
  return (totalTokens / 1000) * 0.01;
}

/**
 * Check if a request falls within the given constraints.
 */
export function isWithinConstraints(request: AIRequest, constraints: AIConstraints): boolean {
  if (constraints.maxTokens !== undefined && request.constraints?.maxTokens !== undefined) {
    if (request.constraints.maxTokens > constraints.maxTokens) return false;
  }
  if (constraints.maxCost !== undefined) {
    // Without model specs we cannot verify cost — assume within bounds
  }
  if (constraints.requiredCapabilities && constraints.requiredCapabilities.length > 0) {
    const requestCaps = request.constraints?.requiredCapabilities ?? [];
    const hasAll = constraints.requiredCapabilities.every((cap) => requestCaps.includes(cap));
    if (!hasAll) return false;
  }
  return true;
}

function estimateInputTokens(request: AIRequest): number {
  let tokens = 50; // base overhead
  if (request.context.fileUri) tokens += 10;
  if (request.context.symbols) tokens += request.context.symbols.length * 5;
  if (request.context.imports) tokens += request.context.imports.length * 8;
  if (request.context.selection) tokens += 200;
  return tokens;
}

// ═══ §4 REQUEST PRIORITY — auto-assignment and comparison ═══

/**
 * Default priority mappings per request type.
 */
export const PRIORITY_MAP: Record<AIRequestType, AIPriority> = {
  completion: 'high',
  suggestion: 'normal',
  analysis: 'normal',
  generation: 'normal',
  conversation: 'normal',
  refactor: 'normal',
  explain: 'low',
  fix: 'critical',
  optimize: 'normal',
  translate: 'normal',
  document: 'low',
  'test-generate': 'normal',
};

const PRIORITY_ORDER: Record<AIPriority, number> = {
  low: 0,
  normal: 1,
  high: 2,
  critical: 3,
};

/**
 * Auto-assign priority based on request type and context.
 */
export function assignPriority(request: AIRequest, context: AIContext): AIRequest {
  let basePriority = PRIORITY_MAP[request.type] ?? 'normal';
  // Elevate priority if we have a selection (user is actively waiting)
  if (context.selection) {
    basePriority = elevatePriority(basePriority);
  }
  return { ...request, priority: basePriority };
}

function elevatePriority(p: AIPriority): AIPriority {
  switch (p) {
    case 'low': return 'normal';
    case 'normal': return 'high';
    case 'high': return 'critical';
    case 'critical': return 'critical';
  }
}

/**
 * Convert a priority to its numeric rank (0-3).
 */
export function priorityToNumber(priority: AIPriority): number {
  return PRIORITY_ORDER[priority];
}

/**
 * Compare two priorities: negative if a < b, zero if equal, positive if a > b.
 */
export function comparePriority(a: AIPriority, b: AIPriority): number {
  return PRIORITY_ORDER[a] - PRIORITY_ORDER[b];
}

/**
 * Sort requests by priority (highest first).
 */
export function sortByPriority(requests: readonly AIRequest[]): AIRequest[] {
  return [...requests].sort((a, b) => PRIORITY_ORDER[b.priority] - PRIORITY_ORDER[a.priority]);
}

/**
 * Determine whether an incoming request should preempt the current one.
 */
export function shouldPreempt(current: AIRequest, incoming: AIRequest): boolean {
  const diff = PRIORITY_ORDER[incoming.priority] - PRIORITY_ORDER[current.priority];
  // Only preempt if incoming is at least 2 levels higher, or is critical and current is not
  if (incoming.priority === 'critical' && current.priority !== 'critical') return true;
  return diff >= 2;
}

// ═══ §5 REQUEST CONTEXT — build and merge AIContext objects ═══

/**
 * Build an AIContext from individual fields.
 */
export function buildContext(
  workspaceId?: WorkspaceId,
  fileUri?: string,
  language?: LanguageId,
  position?: Position,
  selection?: Selection,
): AIContext {
  return {
    workspaceId,
    fileUri,
    language,
    cursorPosition: position,
    selection,
  };
}

/**
 * Merge two contexts, with override winning on conflicts.
 */
export function mergeContexts(base: AIContext, override: Partial<AIContext>): AIContext {
  return {
    workspaceId: override.workspaceId ?? base.workspaceId,
    fileUri: override.fileUri ?? base.fileUri,
    language: override.language ?? base.language,
    cursorPosition: override.cursorPosition ?? base.cursorPosition,
    selection: override.selection ?? base.selection,
    symbols: override.symbols ?? base.symbols,
    imports: override.imports ?? base.imports,
  };
}

/**
 * Extract AI-relevant context from an editor state snapshot.
 * Accepts a minimal shape to avoid importing EditorState.
 */
export function contextFromEditor(editorState: {
  readonly documentId?: string;
  readonly language?: LanguageId;
  readonly cursorPosition?: Position;
  readonly selection?: Selection;
}): AIContext {
  return {
    fileUri: editorState.documentId,
    language: editorState.language,
    cursorPosition: editorState.cursorPosition,
    selection: editorState.selection,
  };
}

/**
 * Extract AI-relevant context from a terminal state snapshot.
 */
export function contextFromTerminal(terminalState: {
  readonly id?: string;
  readonly cwd?: string;
}): AIContext {
  return {
    fileUri: terminalState.cwd,
  };
}

/**
 * Enrich an existing context with symbols and imports.
 */
export function enrichContext(
  context: AIContext,
  symbols: readonly string[],
  imports: readonly string[],
): AIContext {
  return {
    ...context,
    symbols: [...(context.symbols ?? []), ...symbols],
    imports: [...(context.imports ?? []), ...imports],
  };
}

/**
 * Compute a deterministic hash for a context, suitable for dedup or caching.
 */
export function contextHash(context: AIContext): string {
  const parts: string[] = [];
  if (context.workspaceId) parts.push(`w:${context.workspaceId}`);
  if (context.fileUri) parts.push(`f:${context.fileUri}`);
  if (context.language) parts.push(`l:${context.language}`);
  if (context.cursorPosition) parts.push(`p:${context.cursorPosition.line}:${context.cursorPosition.column}`);
  if (context.selection) {
    parts.push(`s:${context.selection.anchor.line}:${context.selection.anchor.column}-${context.selection.active.line}:${context.selection.active.column}`);
  }
  if (context.symbols && context.symbols.length > 0) parts.push(`sym:${context.symbols.join(',')}`);
  if (context.imports && context.imports.length > 0) parts.push(`imp:${context.imports.join(',')}`);
  return simpleHash(parts.join('|'));
}

function simpleHash(str: string): string {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const ch = str.charCodeAt(i);
    hash = ((hash << 5) - hash + ch) | 0;
  }
  return Math.abs(hash).toString(36);
}

// ═══ §6 REQUEST BATCHING — merge compatible requests ═══

/**
 * Request types that can be combined into a single batch.
 */
export const BATCH_COMPATIBLE_TYPES: ReadonlySet<AIRequestType> = new Set<AIRequestType>([
  'completion',
  'suggestion',
  'analysis',
  'explain',
  'document',
]);

/**
 * Determine if two requests can be batched together.
 * Requirements: same module, compatible types, same file context.
 */
export function canBatch(a: AIRequest, b: AIRequest): boolean {
  if (a.module !== b.module) return false;
  if (!BATCH_COMPATIBLE_TYPES.has(a.type) || !BATCH_COMPATIBLE_TYPES.has(b.type)) return false;
  if (a.type !== b.type) return false;
  if (a.context.fileUri !== b.context.fileUri) return false;
  if (a.context.language !== b.context.language) return false;
  return true;
}

/**
 * Batch multiple compatible requests into a single combined request.
 * The combined request uses the highest priority and strictest constraints.
 */
export function batchRequests(requests: readonly AIRequest[]): AIRequest | null {
  if (requests.length === 0) return null;
  if (requests.length === 1) return requests[0] ?? null;

  // Verify all can be batched with the first
  const first = requests[0]!;
  for (let i = 1; i < requests.length; i++) {
    if (!canBatch(first, requests[i]!)) return null;
  }

  // Merge symbols and imports from all contexts
  const allSymbols: string[] = [];
  const allImports: string[] = [];
  for (const req of requests) {
    if (req.context.symbols) allSymbols.push(...req.context.symbols);
    if (req.context.imports) allImports.push(...req.context.imports);
  }

  // Use highest priority
  const highestPriority = requests.reduce<AIPriority>((best, req) => {
    return PRIORITY_ORDER[req.priority] > PRIORITY_ORDER[best] ? req.priority : best;
  }, 'low');

  // Use strictest (smallest) max tokens
  const maxTokens = requests.reduce<number | undefined>((min, req) => {
    const t = req.constraints?.maxTokens;
    if (t === undefined) return min;
    if (min === undefined) return t;
    return Math.min(min, t);
  }, undefined);

  // Use strictest (smallest) max latency
  const maxLatencyMs = requests.reduce<number | undefined>((min, req) => {
    const l = req.constraints?.maxLatencyMs;
    if (l === undefined) return min;
    if (min === undefined) return l;
    return Math.min(min, l);
  }, undefined);

  const mergedContext: AIContext = {
    ...first.context,
    symbols: allSymbols.length > 0 ? [...new Set(allSymbols)] : first.context.symbols,
    imports: allImports.length > 0 ? [...new Set(allImports)] : first.context.imports,
  };

  return {
    id: asAIRequestId(generateId('batch')),
    module: first.module,
    type: first.type,
    context: mergedContext,
    constraints: maxTokens !== undefined || maxLatencyMs !== undefined
      ? { maxTokens, maxLatencyMs }
      : first.constraints,
    priority: highestPriority,
    qualityThreshold: Math.max(...requests.map((r) => r.qualityThreshold)),
    timestamp: now(),
  };
}

/**
 * Split a batched request back into individual requests.
 * Since batching merges context, splitting produces N copies with the merged context
 * but distinct IDs. The count parameter indicates how many original requests were batched.
 */
export function splitBatch(batch: AIRequest, count: number = 2): AIRequest[] {
  const results: AIRequest[] = [];
  for (let i = 0; i < count; i++) {
    results.push({
      ...batch,
      id: asAIRequestId(generateId('aireq')),
      timestamp: now(),
    });
  }
  return results;
}
